<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="profit-loss-main">
    <div class="top-section">
      <div
        ><b>Profit & Loss by {{ reportTitle }}</b></div
      >
      <div style="display: flex; align-items: center; justify-content: flex-end">
        <div class="filter-desc">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="filterIsOpen"
          :buttonIsDisabled="filterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer title="Date" style="margin-bottom: 5px">
              <DateTimePicker
                :isRequired="true"
                :showTime="false"
                placeholder="From"
                :value.sync="fromDate"
                :errorMessage.sync="fromDateErrMsg"
                :minDate="new Date('1900-01-01')"
                :maxDate="new Date('2079-01-01')"
              />
              <DateTimePicker
                :isRequired="true"
                :showTime="false"
                placeholder="To"
                :value.sync="toDate"
                :errorMessage.sync="toDateErrMsg"
                :minDate="new Date('1900-01-01')"
                :maxDate="new Date('2079-01-01')"
              />
            </BoxContainer>
            <Dropdown
              placeholder="Report Type"
              :items="reportTypeItems"
              itemKey="value"
              :selectedObject.sync="selectedReportType"
              :isRequired="true"
              :errorMessage.sync="reportTypeErr"
              validator="text,15"
              style="margin-top: 3px"
            />
            <button @click="refreshClicked" :disabled="!(fromDate && toDate) || !selectedReportType">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
    </div>
    <div class="bottom-section">
      <BoxContainer
        title="Chart"
        :isCollapsible="true"
        style="margin-bottom: 5px; max-height: 90%"
        :isCollapsed.sync="chartIsCollapsed"
      >
        <div v-if="chartIsLoading" class="loading-chart">Loading chart. . .</div>
        <div v-else-if="pnlNoData" class="loading-chart">No Profit & Loss Data Found</div>
        <div class="chart-container">
          <div id="graph-container"> </div>
          <div
            id="legend"
            class="legend-container"
            :style="{ border: chartIsLoading || pnlNoData ? '' : '1px solid #000' }"
          ></div>
        </div>
      </BoxContainer>
      <div :class="pnlTableBoxStyling">
        <!-- :columnWidth="currentReportType == 'branchdept' ? [90, 120, 250] : [250]"
          :additionalButtons="currentReportType == 'branchdept' ? pnlTableButton : []" -->
        <TableBox
          title="Profit and Loss"
          :tableData.sync="pnlTableData"
          :maxSelection="0"
          :loading="pnlTableDataLoading"
          :dataType="pnlTableDataType"
          :columnWidth="[90, 120, 250]"
          :additionalButtons="pnlTableButton"
          @buttonClicked="pnlTableButtonClicked"
          :hideColumns="pnlTableHideColumns"
          :style="pnlTableBoxStyling"
        />
      </div>
    </div>

    <ContextMenu
      v-if="showContextMenu"
      :show.sync="showContextMenu"
      :position="contextPosition"
      :contextItems="contextItems"
      @contextSelected="contextSelected"
      ref="context"
    />
  </div>
</template>

<script>
/*global Chartist*/
export default {
  data: function () {
    return {
      filterText: '',
      lastRefreshed: '',
      filterIsOpen: false,
      filterIsDisabled: false,
      fromDate: new Date(),
      fromDateErrMsg: '',
      toDate: new Date(),
      toDateErrMsg: '',

      reportTypeItems: [
        { key: 'MONTH_COCODE', value: 'Total', item: 'total' },
        { key: 'MONTH_COCODE_CONSOL', value: 'Consol', item: 'consol' },
        { key: 'COCODE', value: 'Branch-Dept', item: 'branchdept' },
      ],
      selectedReportType: null,
      reportTypeErr: '',
      reportTitle: '',

      chartIsCollapsed: false,
      pnlAnalysis: [],
      pnlTableData: [],
      pnlTableDataLoading: false,
      pnlTableDataType: {
        'P&L Item': 'string',
      },
      pnlTableButton: [
        { text: 'Details', index: 0, width: 60 },
        { text: 'Transaction', index: 1, width: 100 },
      ],
      pnlTableHideColumns: [],

      chartIsLoading: false,
      chart: null,
      currentReportType: 'total',
      brDept: [],

      showContextMenu: false,
      contextPosition: [],
      contextItems: ['View Details', 'View Transactions'],
    };
  },
  props: {},
  computed: {
    pnlTableBoxStyling: function () {
      return {
        height: this.chartIsCollapsed ? '95%' : '65%',
        marginBottom: '5px',
      };
    },

    pnlNoData: function () {
      return this.pnlAnalysis.length == 0;
    },
  },
  methods: {
    /**
     * COCODE	--//HQ Consolidation - branch-dept report
     * DETAILS_COCODE	--//HQ Consolidation - with gl acc details - drill through
     * TXN_COCODE --//HQ Consolidation - txn - drill through
     * MONTH_COCODE --//HQ Consolidation - Total by Month - total report
     * MONTH_COCODE_CONSOL --//HQ Consolidation - Consol by Month (exclude internal transactions) - consol report
     * FYI: "Total" = all branch-dept combined; "Consol" = "Total" excluding internal transactions. "Internal" transactions = billing / payments between branch-dept.
     */

    refreshClicked: async function () {
      let error = '';
      if (!this.fromDate) {
        this.fromDateErrMsg = 'From date is required!';
        error = true;
      }

      if (!this.toDate) {
        this.toDateErrMsg = 'To date is required!';
        error = true;
      } else if (this.fromDate && this.fromDate > this.toDate) {
        this.toDateErrMsg = 'To date cannot be earlier than From date!';
      }

      if (!this.selectedReportType) {
        this.reportTypeErr = 'Report Type is required!';
        error = true;
      }

      if (error) return;

      const dataToPost = {
        startdate: this.fromDate ? this.fromDate.formatString('yyyy-MM-dd') : '',
        enddate: this.toDate ? this.toDate.formatString('yyyy-MM-dd') : '',
        rpttype: this.selectedReportType ? this.selectedReportType['key'] : 'MONTH_COCODE',
        rotatetable: this.selectedReportType['key'] === 'COCODE' ? '1' : '0', // if '1', each object will be by pnl item; if '0', each object will be by period
      };

      this.filterText = `Filter by date from ${this.fromDate.formatString('yyyy-MM-dd')} to ${this.toDate.formatString('yyyy-MM-dd')}`;
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
      this.filterIsOpen = false;

      // set currentReportType instead of using selectedReportType to prevent any changes if user changes the report type in filter without refreshing
      switch (this.selectedReportType['key']) {
        case 'MONTH_COCODE':
          this.reportTitle = 'Month (Total)';
          break;

        case 'MONTH_COCODE_CONSOL':
          this.reportTitle = 'Month (Consol)';
          break;

        case 'COCODE':
          this.reportTitle = 'Branch-Dept';
          break;

        default:
          this.reportTitle = 'Month';
          break;
      }
      this.currentReportType = this.selectedReportType['item'] ?? '';

      this.pnlTableDataLoading = true;
      this.pnlTableData = [];
      this.chartIsLoading = true;

      if (this.chart) {
        this.chart.detach();
        this.chart = null;
      }

      document.getElementById('graph-container').innerHTML = '';
      document.getElementById('legend').innerHTML = '';

      this.$fullscreenLoading('Getting PNL Analysis');
      this.pnlAnalysis = await this.$apiCallV2('/HQConsolidatedProfitLoss/GetPNLAnalysis', dataToPost);
      if (this.pnlAnalysis.length == 0) {
        this.chartIsLoading = false;
        this.pnlTableDataLoading = false;
        this.$fullscreenLoading();
        return;
      }

      await this.generatePNLTable(); // so that pnlTableData (nexttick) is set before creating chart

      this.generateChart(this.selectedReportType['key']);

      this.$fullscreenLoading();
    },

    pnlTableButtonClicked: function (btn, data) {
      const dateRangeKey = this.getDateRangeKey();
      this.$fullscreenLoading('Getting Details');

      const series =
        data ?
          this.currentReportType == 'branchdept' ?
            data[dateRangeKey]
          : this.removeTag(data['P&L Item'])
        : '';

      const dataToPost = {
        startdate: this.$dateTime.getDateTime(this.fromDate, 'yyyy-MM-dd'),
        enddate: this.$dateTime.getDateTime(this.toDate, 'yyyy-MM-dd'),
        rpttype: `${btn == 'Details' ? 'DETAILS' : 'TXN'}_COCODE`,
        series: series || '',
        rotatetable: '1',
      };

      const pnlItem =
        data ?
          series.includes('/') ?
            series.replace('/', ' ')
          : series
        : '';

      const drillThroughTitle = `Drill Through - ${pnlItem} (${btn})`;
      sessionStorage.setItem(`${drillThroughTitle}_HQPNL`, JSON.stringify({ data: dataToPost, series: pnlItem }));

      const drillThroughLink = this.$router.resolve(
        `/Home/${this.$route.params['user']}/FINGL/HQConsolidated/HQConsolidatedProfitLoss/${drillThroughTitle}`,
      );
      window.open(drillThroughLink.href, '_blank');

      this.$fullscreenLoading();
    },

    generatePNLTable: function () {
      this.pnlTableHideColumns = [];
      // reset datatype
      this.pnlTableDataType = {
        'P&L Item': 'string',
      };

      const pnlAnalysisCopy = this.pnlAnalysis;
      let formattedPNLTable = [];
      if (this.currentReportType == 'branchdept') {
        // branch-dept report has buttons to view each item's details/transactions in drill through page (refer HQProfitLossDrillThrough.vue)
        const dateRangeKey = this.getDateRangeKey();
        this.pnlTableHideColumns = ['#', 'ProfitLoss', 'PNLSeq', 'Operation', dateRangeKey];

        formattedPNLTable = pnlAnalysisCopy.map((pnl) => {
          return {
            'P&L Item': `<b>${pnl[dateRangeKey]}</b>`,
            ...pnl,
          };
        });

        // Set all amount columns to currency type
        Object.keys(formattedPNLTable[0]).forEach((key) => {
          if (!this.pnlTableHideColumns.includes(key) && key !== 'P&L Item') {
            this.pnlTableDataType[key] = 'currency';
          }
        });
      } else {
        const keys = Object.keys(pnlAnalysisCopy[0]).filter((k) => !['#', 'XAxis', 'XAxisDesc', 'ProfitLoss'].includes(k));
        // each key in the json returned from API is a P&L item (except for branch-dept report)
        formattedPNLTable = keys.map((k) => {
          return {
            'P&L Item': `<b>${k}</b>`,
          };
        });

        // XAxisDesc = Period (MM yyyy)
        pnlAnalysisCopy.forEach((pnl) => {
          formattedPNLTable.forEach((row, j) => {
            row[pnl['XAxisDesc']] = pnl[keys[j]] || '0.00';
          });
        });

        // Set all amount columns to currency type
        Object.keys(formattedPNLTable[0]).forEach((key) => {
          if (key !== 'P&L Item') {
            this.pnlTableDataType[key] = 'currency';
          }
        });
      }

      this.$nextTick(() => {
        this.pnlTableData = formattedPNLTable;
        this.pnlTableDataLoading = false;
      });
    },

    removeTag: function (str) {
      const tempdiv = document.createElement('div');
      tempdiv.innerHTML = str;
      return tempdiv.textContent;
    },

    generateChart: function (item) {
      this.$fullscreenLoading('Getting Details');
      const pnlDataCopy = this.pnlAnalysis;

      if (item === 'COCODE') {
        // Bar chart to show only items with operations (+/-) (gross/net items)
        const keywords = ['net', 'gross'];
        let operations = pnlDataCopy.map((p) => p[this.getDateRangeKey()]);
        operations = operations.filter((item) => keywords.some((keyword) => item.toLowerCase().includes(keyword)));

        const data = operations.map((op) => {
          const values = pnlDataCopy.find((pnl) => pnl[this.getDateRangeKey()] === op);
          const amounts = this.brDept.map((dept) => {
            return {
              dept: dept,
              value: values[dept] || 0,
            };
          });

          return {
            name: op,
            data: amounts.map((amt) => {
              return {
                // meta used for tooltip
                // format in-use: Br-Dept <br> Item: Amount
                meta: `${amt.dept}<br>${op}: ${this.$currencyFormat(amt.value.toString().replaceAll(',', '') || '0')}`,
                value: amt,
              };
            }),
          };
        });

        this.generateBarChart(this.brDept, data);
      } else {
        const xLabels = pnlDataCopy.map((p) => p['XAxisDesc']);

        const dataPoints = this.pnlTableData.map((pnl) => {
          return {
            name: this.removeTag(pnl['P&L Item']),
            data: xLabels.map((label) => {
              return {
                meta: `${label}: ${this.$currencyFormat(pnl[label].toString().replaceAll(',', '') || '0')}`,
                value: pnl[label] || 0,
              };
            }),
          };
        });
        this.generateLineChart(xLabels, dataPoints);
      }
      this.chartIsLoading = false;
    },

    generateLineChart: function (xLabels, dataPoints) {
      document.getElementById('graph-container').innerHTML =
        '<div class="ct-chart-PL" id="chart" style="width: 100%; height: 100%"></div>';

      const data = {
        labels: xLabels,
        series: dataPoints,
      };

      const options = {
        chartPadding: { top: 30, right: 30, bottom: 30, left: 30 },
        height: '450px',
        width: '100%',
        axisY: { scaleMinSpace: 20 },
        axisX: { showLabel: true },
        plugins: [
          Chartist.plugins.legend({
            position: document.getElementById('legend'),
            clickable: true,
            className: 'PL',
          }),
        ],
      };

      const tooltip = document.createElement('div');
      tooltip.className = 'tooltip tooltip-hidden';
      document.querySelector('.ct-chart-PL').appendChild(tooltip);

      const lineGraph = new Chartist.Line('.ct-chart-PL', data, options).on('draw', function (data) {
        // Add event listeners for tooltip on each point
        if (data.type == 'point') {
          data.element._node.addEventListener('mouseenter', (_e) => {
            tooltip.innerHTML = `<span>${data.series.name}<br/>${data.meta}</span>`;
            tooltip.classList.remove('tooltip-hidden');
          });

          data.element._node.addEventListener('mouseleave', (_e) => {
            tooltip.classList.add('tooltip-hidden');
          });

          data.element._node.addEventListener('mousemove', (e) => {
            tooltip.style.left = `${e.pageX - 60}px`;
            tooltip.style.top = `${e.pageY - 100}px`;
          });
        }
      });

      // Add click event to the chart container to show drill through options
      document.querySelector('.ct-chart-PL').addEventListener('click', (e) => this.chartOnClick(e));

      this.chart = lineGraph;
    },

    // Uncomment if you want to generate pie chart
    // generatePieChart: function (labels, chartData) {
    //   document.getElementById('graph-container').innerHTML =
    //     '<div class="ct-chart-PL pie-chartHQ" id="chart" style="width: 100%; height: 100%"></div>';

    //   const data = {
    //     labels: labels,
    //     series: chartData,
    //   };

    //   const options = {
    //     donut: false,
    //     chartPadding: 30,
    //     height: '400px',
    //     width: '100%',
    //     labelInterpolationFnc: (label, seriesIndex) => {
    //       const total = chartData.reduce((acc, row) => acc + Number(row['value']), 0);
    //       if (!total) return label;
    //       const percentage = ((Number(chartData[seriesIndex]['value']) / total) * 100).toFixed(2);
    //       return `${percentage}%`;
    //     },
    //     plugins: [
    //       Chartist.plugins.legend({
    //         position: document.getElementById('legend'),
    //         clickable: true,
    //         className: 'PL',
    //       }),
    //     ],
    //   };

    //   const pieChart = new Chartist.Pie(`.ct-chart-PL`, data, options);
    //   this.chart = pieChart;
    //   console.log('Pie chart created', this.chart);
    // },

    generateBarChart: function (xLabels, dataPoints) {
      document.getElementById('graph-container').innerHTML =
        '<div class="ct-chart-PL bar-chartHQ" id="chart" style="width: 100%; height: 100%"></div>';

      const data = {
        labels: xLabels,
        series: dataPoints,
      };

      const options = {
        chartPadding: { top: 30, right: 30, bottom: 30, left: 30 },
        height: '450px',
        width: '100%',
        axisY: { scaleMinSpace: 20 },
        axisX: { showLabel: true, offset: 60 },
        plugins: [
          Chartist.plugins.legend({
            position: document.getElementById('legend'),
            clickable: true,
            className: 'PL',
          }),
        ],
      };

      const tooltip = document.createElement('div');
      tooltip.className = 'tooltip tooltip-hidden';
      document.querySelector('.ct-chart-PL').appendChild(tooltip);

      const barGraph = new Chartist.Bar('.ct-chart-PL', data, options).on('draw', function (data) {
        // Add event listeners for tooltip on each bar
        if (data.type == 'bar') {
          data.element._node.addEventListener('mouseenter', (_e) => {
            const meta = data.meta.split('<br>');
            tooltip.innerHTML = `<span>${meta[0]}<br/>${meta[1]}</span>`;
            tooltip.classList.remove('tooltip-hidden');
          });

          data.element._node.addEventListener('mouseleave', (_e) => {
            tooltip.classList.add('tooltip-hidden');
          });

          data.element._node.addEventListener('mousemove', (e) => {
            tooltip.style.left = `${e.pageX - 60}px`;
            tooltip.style.top = `${e.pageY - 100}px`;
          });
        }
      });

      // Add click event to the chart container to show drill through options
      document.querySelector('.ct-chart-PL').addEventListener('click', (e) => this.chartOnClick(e));

      this.chart = barGraph;
    },

    chartOnClick: function (e) {
      this.contextPosition = [e.clientY - 150, e.clientX - 60];
      this.showContextMenu = true;
    },

    // To get object key that holds the pnl item desc (when rotateTable is '1')
    getDateRangeKey: function () {
      const from = this.fromDate.formatString('dd/MM/yy');
      const to = this.toDate.formatString('dd/MM/yy');
      return `${from}-${to}`;
    },

    contextSelected: function (context) {
      const button = context == 'View Details' ? 'Details' : 'Transaction';
      this.pnlTableButtonClicked(button);
    },
  },
  watch: {},
  async mounted() {
    const mountedPromises = [];
    let promisesFulfilled = 0;

    const currentDate = new Date();
    let startFyearDate = null;
    let endFyearDate = null;

    mountedPromises.push(
      this.$apiCallV2('/HQConsolidatedProfitLoss/GetPeriodDate', {
        date: new Date().formatString('yyyy-MM-dd'),
        period: '1',
        monthend: '',
      }).then((result) => {
        startFyearDate = result[0]?.['periodDate'];
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/HQConsolidatedProfitLoss/GetPeriodDate', {
        date: new Date().formatString('yyyy-MM-dd'),
        period: '12',
        monthend: '1',
      }).then((result) => {
        endFyearDate = result[0]?.['periodDate'];
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/HQConsolidatedProfitLoss/GetHQBranchDept').then((result) => {
        this.brDept = result.map((b) => b['BrDept']);
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(() => {
      this.fromDate = new Date(startFyearDate) ?? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      this.toDate = new Date(endFyearDate) ?? new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    });

    this.selectedReportType = this.reportTypeItems[0];
    await this.refreshClicked();
  },
};
</script>

<style lang="scss" scoped>
.profit-loss-main {
  padding: 10px 10px 10px 45px;
  height: 100%;
  border-radius: 5px;
  font-size: 12px;

  > .top-section,
  > .bottom-section {
    background-color: white;
    border-radius: 5px;
    padding: 5px;
  }

  > .top-section {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .filter-desc {
      padding-right: 8px;
    }

    .filter-popup .button-container {
      width: calc(100% - 300px);

      > button {
        height: fit-content;
        margin: 1px 3px;
      }
    }

    > .refresh-last {
      width: 300px;
      font-size: 12px;
      text-align: end;
      padding: 5px;
    }
  }

  > .bottom-section {
    height: fit-content;
    margin-top: 5px;
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.chart-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  #graph-container {
    width: 100%;
    height: 100%;
  }

  .legend-container {
    margin-right: 10px;
    width: 15%;
    height: fit-content;
    border-radius: 5px;
    padding: 10px;
    margin-bottom: 5px;

    ::v-deep ul {
      padding-left: 0px;
    }
  }
}

.loading-chart {
  width: 100%;
  text-align: center;
  background-color: rgba(150, 150, 150, 0.2);
  height: calc(100% - 40px);
  width: 100%;
}
</style>
